/**
 * Admin Level Column Definitions
 *
 * Column definitions for the AdminLevel DataTable.
 */

import { type ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit, Trash2, Shield } from "lucide-react";
import type { AdminLevelItem } from "@/services/adminService";
import { getLevelLabel, getLevelIcon, getLevelColor, formatPrivilege } from "../utils/adminLevelUtils";
import { cn } from "@/lib/utils";

interface AdminLevelColumnCallbacks {
  onEdit: (item: AdminLevelItem) => void;
  onDelete: (item: AdminLevelItem) => void;
}

export function createAdminLevelColumns({
  onEdit,
  onDelete,
}: AdminLevelColumnCallbacks): ColumnDef<AdminLevelItem>[] {
  return [
    {
      id: "icon",
      header: () => null,
      cell: () => (
        <div className="flex items-center justify-center">
          <Shield className="h-4 w-4 text-muted-foreground" />
        </div>
      ),
      enableSorting: false,
      size: 50,
    },
    {
      accessorKey: "name",
      header: "Nama",
      cell: ({ row }) => (
        <div className="flex flex-col gap-1">
          <span className="font-medium">{row.original.name}</span>
          {row.original.description && (
            <span className="text-xs text-muted-foreground line-clamp-1">
              {row.original.description}
            </span>
          )}
        </div>
      ),
      enableSorting: true,
    },
    {
      accessorKey: "level",
      header: "Level",
      cell: ({ row }) => {
        const level = row.original.level;
        const Icon = getLevelIcon(level);
        return (
          <Badge
            variant="outline"
            className={cn("inline-flex items-center gap-1.5", getLevelColor(level))}
          >
            <Icon className="h-3 w-3" />
            {getLevelLabel(level)}
          </Badge>
        );
      },
      enableSorting: true,
    },
    {
      accessorKey: "order",
      header: "Urutan",
      cell: ({ row }) => (
        <span className="font-mono text-sm">{row.original.order}</span>
      ),
      enableSorting: true,
    },
    {
      id: "privileges",
      accessorFn: (row) => row.defaultPrivileges?.length ?? 0,
      header: "Hak Akses Default",
      cell: ({ row }) => {
        const privileges = row.original.defaultPrivileges;
        if (!privileges || privileges.length === 0) {
          return <span className="text-muted-foreground text-sm">-</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {privileges.slice(0, 3).map((p) => (
              <Badge key={p} variant="outline" className="text-xs">
                {formatPrivilege(p)}
              </Badge>
            ))}
            {privileges.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{privileges.length - 3}
              </Badge>
            )}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      id: "actions",
      header: "Aksi",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(row.original)}
            className="h-8 w-8 p-0"
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDelete(row.original)}
            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
      enableSorting: false,
      size: 100,
    },
  ];
}
